import { PageLoadingIndicator } from '@wrisk/ui-components'
import React, {
  FunctionComponent,
  PropsWithChildren,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { AnonymousUser, RetrieveRequest } from '../../../clients/anonymous'
import { User } from '../../../clients/auth'
import { useAuth } from '../../../hooks/auth'
import airbrake from '../../../infrastructure/airbrake/airbrake'
import { useAnonymous, useApiClientFactory } from '../../../state'
import { useAuthenticate } from '../hooks'
import { PolicyholderProvider } from '../policyholder'
import PrincipalProvider, { Principal } from '../PrincipalProvider'

export const isUser = (user: User | AnonymousUser | undefined): user is User =>
  Boolean(user && (user as User).username)

interface PrincipalManagerProps extends PropsWithChildren {
  user: User | AnonymousUser
}

const PrincipalManager: FunctionComponent<PrincipalManagerProps> = ({
  user,
  children,
}) => {
  const [userState, setUser] = useState<User | AnonymousUser>(user)

  const apiClientFactory = useApiClientFactory()
  const anonymous = useAnonymous()
  const auth = useAuth()

  const principal = useMemo<Principal>(
    () =>
      isUser(userState)
        ? {
            user: userState,
            isAuthenticated: true,
            isAdmin: false,
            apiClient: apiClientFactory(auth.getTokenSilently),
            refreshAnonymous: async () => {
              await anonymous.refreshToken()
            },
          }
        : {
            user: userState,
            isAuthenticated: false,
            isAdmin: false,
            apiClient: apiClientFactory(anonymous.getToken),
            retrieveAnonymous: async (request: RetrieveRequest) => {
              setUser(await anonymous.retrieveToken(request))
            },
            refreshAnonymous: async () => {
              setUser(await anonymous.refreshToken())
            },
          },
    [userState],
  )

  useEffect(() => {
    userState &&
      airbrake?.addFilter((notice) => {
        notice.context.user = {
          id: userState.userId,
        }
        return notice
      })

    window.dataLayer?.push({
      userId: userState?.userId,
    })
  }, [userState])

  return (
    <PrincipalProvider value={principal}>
      <PolicyholderProvider>{children}</PolicyholderProvider>
    </PrincipalProvider>
  )
}

export const UserManager: FunctionComponent<PropsWithChildren> = ({ children }) => {
  const { loading, result: user } = useAuthenticate(true)

  return loading ? (
    <PageLoadingIndicator />
  ) : user ? (
    <PrincipalManager user={user}>{children}</PrincipalManager>
  ) : null
}
